import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/react';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import CcToEcRedirectFlagSync from 'providers/ccToEcRedirectFlagSync/index.tsx';
import FullStoryInitializer from 'providers/fullStoryInitializer/index.tsx';
import ReactQueryClientProvider from 'providers/reactQueryClient/index.tsx';
import MuiThemeProvider from 'providers/theme/index.tsx';
import type { ComponentType, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import 'react-toastify/dist/ReactToastify.css';
import App from './App.tsx';
import './styles/index.css';

/**
 * Error handler for webkit messageHandlers errors. See ENG-5747 for context.
 *
 * Third-party scripts may call window.webkit.messageHandlers[x].postMessage() expecting
 * an iOS WebView environment. On desktop browsers, window.webkit is undefined, causing:
 * "TypeError: undefined is not an object (evaluating 'window.webkit.messageHandlers[t].postMessage')"
 *
 * We suppress these errors rather than polyfilling window.webkit, because modifying that
 * global property can break environment detection in other libraries (e.g., FullStory).
 */
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.message && event.message.includes('webkit.messageHandlers')) {
      console.warn('Webkit messageHandlers error caught and handled:', event.message);
      event.preventDefault(); // Prevent the error from propagating
    }
  });
}

const isLocal = window.location.hostname === 'localhost';

const ignoreErrors = [
  'Load failed',
  'Failed to fetch',
  'Fetch error',
  'Script error.',
  'NetworkError when attempting to fetch resource.',
  'ResizeObserver loop completed with undelivered notifications.',
  'https://px.ads.linkedin.com',
  'https://tags.srv.stackadapt.com',
  'window.lintrk is not a function',
  'Non-Error promise rejection captured',
];

if (!isLocal) {
  // there's a vite plugin we could use instead, but it seems new / not well maintained
  datadogLogs.init({
    clientToken: 'pubebb2bb01610f86fdfcae57fed5c79210',
    site: 'us5.datadoghq.com',
    service: 'consumer-portal',
    env: import.meta.env.MODE === 'production' ? 'prod' : import.meta.env.MODE,
    version: import.meta.env.REACT_APP_VERSION,
    forwardConsoleLogs: 'all',
    forwardReports: 'all',
    trackAnonymousUser: true,
    useSecureSessionCookie: true,
    beforeSend: (log) => {
      // discard specific errors
      if (log.message && ignoreErrors.some((error) => log.message.includes(error))) {
        return false;
      }

      // discard 404 network errors
      if (log.http && log.http.status_code === 404) {
        return false;
      }

      return true;
    },
  });

  datadogRum.init({
    applicationId: '0fcfb593-096a-4811-a98c-652c27fc00bd',
    clientToken: 'pubebb2bb01610f86fdfcae57fed5c79210',
    site: 'us5.datadoghq.com',
    service: 'consumer-portal',
    env: import.meta.env.MODE === 'production' ? 'prod' : import.meta.env.MODE,
    version: import.meta.env.REACT_APP_VERSION,
    useSecureSessionCookie: true,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: (event) => {
      // discard specific errors

      const errorMessage =
        event.type === 'error' && event.error && typeof event.error.message === 'string'
          ? event.error.message
          : '';
      if (event.type === 'error' && ignoreErrors.some((error) => errorMessage.includes(error))) {
        return false;
      }

      // Suppress errors from browser extensions (e.g. injected-scripts/host-additional-hooks.js),
      // not from our application code. See ENG-9841.
      if (
        event.type === 'error' &&
        errorMessage.includes('mobx-state-tree') &&
        event.error?.stack?.includes('/injected-scripts/')
      ) {
        if (import.meta.env.MODE !== 'production') {
          console.debug('[datadogRum] Dropped browser-extension error (ENG-9841):', errorMessage);
        }
        return false;
      }
      // discard 404 network errors
      if (event.type === 'resource' && event?.resource?.status_code === 404) {
        return false;
      }

      return true;
    },
  });

  Sentry.init({
    dsn: import.meta.env.REACT_APP_SENTRY_DSN ?? '',
    tracesSampleRate: 0,
    release: `${import.meta.env.MODE === 'production' ? 'prod' : (import.meta.env.MODE ?? '')}@${import.meta.env.REACT_APP_VERSION}`,
    dist: import.meta.env.MODE === 'production' ? 'prod' : (import.meta.env.MODE ?? ''),
    environment: import.meta.env.MODE === 'production' ? 'prod' : (import.meta.env.MODE ?? ''),
    ignoreErrors,
    beforeSend: (event, hint) => {
      const originalException = hint.originalException;
      const message = originalException instanceof Error ? originalException.message : '';
      const stack = originalException instanceof Error ? (originalException.stack ?? '') : '';

      const responseStatus = (originalException as { response?: { status?: number } } | null)
        ?.response?.status;

      if (responseStatus !== undefined && responseStatus >= 400) {
        return null;
      }

      // Ignore webkit-related errors as they are handled by the error handler
      const errorMessage = event.exception?.values?.[0]?.value || '';
      if (errorMessage.includes('webkit.messageHandlers')) {
        return null;
      }
      // Suppress errors from browser extensions (e.g. injected-scripts/host-additional-hooks.js),
      // not from our application code. See ENG-9841.
      if (message.includes('mobx-state-tree') && stack.includes('/injected-scripts/')) {
        if (import.meta.env.MODE !== 'production') {
          console.debug('[Sentry] Dropped browser-extension error (ENG-9841):', message);
        }
        return null;
      }

      return event;
    },
  });
}

const AppRoot = (): ReactNode => (
  <ReactQueryClientProvider>
    <FullStoryInitializer />
    <CcToEcRedirectFlagSync />
    <MuiThemeProvider>
      <App />
    </MuiThemeProvider>
  </ReactQueryClientProvider>
);

const launchDarklyClientSideId = import.meta.env.REACT_APP_LAUNCHDARKLY_CLIENT_SIDE_ID ?? '';

const LaunchDarklyProvider = launchDarklyClientSideId
  ? withLDProvider({
      clientSideID: launchDarklyClientSideId,
      context: { kind: 'user', key: 'consumer-portal', anonymous: true },
      timeout: 2,
      reactOptions: {
        useCamelCaseFlagKeys: false,
      },
    })
  : null;

const Root: ComponentType = LaunchDarklyProvider ? LaunchDarklyProvider(AppRoot) : AppRoot;

createRoot(document.getElementById('root')!).render(<Root />);
